/**
 * Property Search
 * Real-time search, filtering, and keyboard navigation for the CSS properties sidebar.
 */

const DEBOUNCE_DELAY = 150;
const HEADER_OFFSET = 120;

class PropertySearch {
	input: HTMLInputElement;
	sidebar: any;
	scrollContainer: any;
	sidebarHeader: any;
	scrollToActiveBtn: HTMLElement;
	items: HTMLElement[];
	pageActiveItem: any;
	visibleItems: any[];
	activeIndex: number;
	userInput: string;
	constructor() {
		this.input = document.getElementById("property-search") as HTMLInputElement;
		this.sidebar = document.querySelector(".sidebar");
		this.scrollContainer = document.querySelector(".sidebar-nav");
		this.sidebarHeader = document.querySelector(".sidebar-header");
		this.scrollToActiveBtn = document.getElementById("scroll-to-active");

		this.items = Array.from(document.querySelectorAll(".nav-item"));
		this.pageActiveItem = document.querySelector(".nav-item.active");

		this.visibleItems = [];
		this.activeIndex = -1;
		this.userInput = "";
	}

	init() {
		if (!this.input) return;

		this.bindEvents();
		this.initScrollToActive();
	}

	// --- Event Binding ---

	bindEvents() {
		const debouncedFilter = this.debounce(
			(query) => this.filter(query),
			DEBOUNCE_DELAY,
		);

		this.input.addEventListener("input", (e) => {
			this.userInput = this.extractUserInput(e.target);
			debouncedFilter(this.userInput);
		});

		this.input.addEventListener("keypress", (e) => this.handleKeypress(e));
		this.input.addEventListener("keydown", (e) => this.handleKeydown(e));
		this.input.addEventListener("blur", () => this.handleBlur());

		document.addEventListener("keydown", (e) => this.handleGlobalKeydown(e));
		document.addEventListener("click", (e) => this.handleOutsideClick(e));
	}

	// --- Filtering ---

	filter(query) {
		const term = query.toLowerCase().trim();
		this.visibleItems = [];

		for (const item of this.items) {
			const name = item.dataset.entry.toLowerCase();
			const matches = !term || name.includes(term);

			item.classList.toggle("hidden", !matches);
			item.classList.remove("active");

			if (matches) {
				this.visibleItems.push(item);
				item.classList.toggle("highlight", term && name.startsWith(term));
			} else {
				item.classList.remove("highlight");
			}
		}

		if (term && this.visibleItems.length > 0) {
			this.activeIndex = 0;
			this.updateActiveItem();
			this.autocomplete();
			requestAnimationFrame(() => this.scrollToItem(this.visibleItems[0]));
		} else {
			this.activeIndex = -1;
			this.updateActiveItem();
		}
	}

	// --- Navigation ---

	navigate(direction) {
		if (this.visibleItems.length === 0) return;

		this.activeIndex =
			direction === "down"
				? Math.min(this.activeIndex + 1, this.visibleItems.length - 1)
				: Math.max(this.activeIndex - 1, -1);

		this.updateActiveItem();
		this.autocomplete();
	}

	updateActiveItem() {
		for (const item of this.items) {
			item.classList.remove("active");
		}

		const activeItem = this.visibleItems[this.activeIndex];

		if (activeItem) {
			activeItem.classList.add("active");
			this.input.setAttribute("aria-activedescendant", activeItem.id);
			this.scrollToItem(activeItem);
		} else {
			this.input.removeAttribute("aria-activedescendant");
			this.pageActiveItem?.classList.add("active");
		}
	}

	// --- Autocomplete ---

	autocomplete() {
		const activeItem = this.visibleItems[this.activeIndex];
		if (!activeItem || !this.userInput) return;

		const entryName = activeItem.dataset.entry;
		if (!entryName.toLowerCase().startsWith(this.userInput.toLowerCase()))
			return;

		this.input.value = this.userInput + entryName.slice(this.userInput.length);
		this.input.setSelectionRange(
			this.userInput.length,
			this.input.value.length,
		);
	}

	extractUserInput(input) {
		const { selectionStart, selectionEnd, value } = input;
		return selectionStart === value.length || selectionStart === selectionEnd
			? value
			: this.userInput;
	}

	// --- Scrolling ---

	scrollToItem(item) {
		if (!item || !this.scrollContainer) return;

		const containerRect = this.scrollContainer.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();
		const relativeTop = itemRect.top - containerRect.top;
		const relativeBottom = itemRect.bottom - containerRect.top;

		const isOutOfView =
			relativeTop < HEADER_OFFSET || relativeBottom > containerRect.height;

		if (isOutOfView) {
			this.scrollContainer.scrollTo({
				top: this.scrollContainer.scrollTop + relativeTop - HEADER_OFFSET,
				behavior: "smooth",
			});
		}
	}

	initScrollToActive() {
		if (!this.pageActiveItem || !this.scrollContainer || !this.sidebarHeader)
			return;

		const updateVisibility = () => {
			const containerRect = this.scrollContainer.getBoundingClientRect();
			const itemRect = this.pageActiveItem.getBoundingClientRect();

			const isVisible =
				itemRect.top >= containerRect.top &&
				itemRect.bottom <= containerRect.bottom;

			this.scrollToActiveBtn?.toggleAttribute("hidden", isVisible);
		};

		requestAnimationFrame(() => {
			updateVisibility();

			if (!this.scrollToActiveBtn?.hidden) {
				this.pageActiveItem.scrollIntoView({
					behavior: "instant",
					block: "start",
				});
			}
		});

		this.scrollToActiveBtn?.addEventListener("click", () => {
			const containerTop = this.scrollContainer.getBoundingClientRect().top;
			const itemTop =
				this.pageActiveItem.getBoundingClientRect().top - containerTop;

			this.scrollContainer.scrollTo({
				top: Math.max(this.scrollContainer.scrollTop + itemTop - 8, 0),
				behavior: "smooth",
			});
		});

		this.scrollContainer.addEventListener("scroll", updateVisibility);
		window.addEventListener("resize", updateVisibility);
	}

	// --- Keyboard Handlers ---

	handleKeydown(e) {
		switch (e.key) {
			case "Escape":
				this.reset();
				this.input.blur();
				break;
			case "ArrowDown":
				e.preventDefault();
				this.navigate("down");
				break;
			case "ArrowUp":
				e.preventDefault();
				this.navigate("up");
				break;
			case "Backspace":
			case "Delete":
				if (this.input.selectionStart !== this.input.selectionEnd) {
					setTimeout(() => {
						this.userInput = this.input.value;
					}, 0);
				}
				break;
			case "Enter":
				e.preventDefault();
				this.activateCurrentItem();
				break;
		}
	}

	handleKeypress(e) {
		const hasSelection = this.input.selectionStart !== this.input.selectionEnd;
		if (hasSelection && e.key.length === 1) {
			setTimeout(() => {
				this.userInput = this.input.value;
			}, 0);
		}
	}

	handleGlobalKeydown(e) {
		if ((e.ctrlKey || e.metaKey) && e.key === "k") {
			e.preventDefault();
			this.input.focus();
			this.input.select();
			this.userInput = this.input.value;
		}
	}

	activateCurrentItem() {
		const item = this.visibleItems[this.activeIndex];
		const link =
			item?.querySelector(".property-link") ||
			document.querySelector(".nav-item.highlight .property-link");
		link?.click();
	}

	// --- Mobile / Focus Handling ---

	handleBlur() {
		if (this.userInput && this.input.value !== this.userInput) {
			this.input.value = this.userInput;
		}
	}

	handleOutsideClick(e) {
		if (this.input.contains(e.target) || this.sidebar.contains(e.target))
			return;

		for (const item of this.items) {
			item.classList.remove("highlight");
		}
	}

	// --- Utilities ---

	reset() {
		this.input.value = "";
		this.userInput = "";
		this.activeIndex = -1;
		this.filter("");
	}

	debounce(fn: (...args: any[]) => void, delay: number) {
		let timer: number;
		return (...args: any[]) => {
			clearTimeout(timer);
			timer = window.setTimeout(() => fn(...args), delay);
		};
	}
}

// Initialize on DOM ready
const initSearch = () => new PropertySearch().init();

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initSearch);
} else {
	initSearch();
}
